import { useIsSmartPlannerView } from "PFApp/booking/hooks/use_is_smart_planner_view";
import { ReplaceBookingsRangeResponse } from "PFApp/booking/services/api";
import { useBookingModuleStore, useCalendarRoot } from "PFApp/booking/store";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { isProjects } from "PFApp/booking/utils/calendar_root.utils";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useBookingsReplaceRange } from "PFCore/hooks/queries/bookings/use_bookings_replace_range";
import { CalendarGranularity } from "PFCore/hooks/queries/profile/use_profiles_availabilities";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useBookingEditModeStoreActions, useBookingEditModeStoreContext } from "../booking_edit_mode_store";
import { PendingEditStatus } from "../booking_edit_mode_store.types";
import { createPayload, groupEditsByActivityAndProfile } from "./use_pending_edits_sync.utils";

const SYNC_DEBOUNCE_MS = 2000;
const SYNC_MAX_WAIT_MS = 4000;

const PENDING_EDITS_ACTION_ID = getGrowlId("bm-pending_edits_action");

const GROWL_TTL_MS = 5000;

export const usePendingEditsSync = () => {
  const store = useBookingEditModeStoreContext();
  const { smartPlannerMode } = useBookingEditModeStoreActions();

  const { t } = useTranslation("bookingModule");
  const { showGrowl } = useGrowlStoreActions();
  const growlErrors = useErrorsGrowl();
  const isSmartPlannerView = useIsSmartPlannerView();

  const calendarRoot = useCalendarRoot();
  const calendarGranularity = useBookingModuleStore((state) => {
    if (isProjects(calendarRoot)) {
      return calendarRoot === CalendarRoot.ProjectsEngagements
        ? state[CalendarRoot.ProjectsEngagements].calendarGranularity
        : state[CalendarRoot.ProjectsRoles].calendarGranularity;
    }
    return state[CalendarRoot.WorkforceProfiles].calendarGranularity;
  });

  const { replaceRange, mutation } = useBookingsReplaceRange({
    onSuccess: (response) => {
      const messages: string[] = [];
      if (response.createdBookings.length > 0) {
        messages.push(
          t("smartPlanner.success.bookingsCreated", {
            count: response.createdBookings.length
          })
        );
      }
      if (response.updatedBookings.length > 0) {
        messages.push(
          t("smartPlanner.success.bookingsUpdated", {
            count: response.updatedBookings.length
          })
        );
      }
      if (response.deletedBookings.length > 0) {
        messages.push(
          t("smartPlanner.success.bookingsDeleted", {
            count: response.deletedBookings.length
          })
        );
      }
      if (messages.length > 0) {
        showGrowl({
          message: messages.join(", "),
          kind: "success",
          id: PENDING_EDITS_ACTION_ID,
          ttl: GROWL_TTL_MS,
          hidePreviousByTypeAndId: true
        });
      }
    },
    onError: ({ response }) => {
      growlErrors(response);
    }
  });

  const executeSync = useCallback(async () => {
    const { pendingEdits } = store.getState().smartPlannerMode;

    const pendingEntries = Object.entries(pendingEdits).filter(
      ([, edit]) => edit.status === PendingEditStatus.Pending
    );

    if (!isSmartPlannerView || mutation.isPending || pendingEntries.length === 0) {
      return;
    }

    const groupedEdits = groupEditsByActivityAndProfile(pendingEntries);
    const allEditKeys: string[] = [];
    const failedKeys = new Set<string>();
    const responses: ReplaceBookingsRangeResponse[] = [];

    const mutationPromises = Array.from(groupedEdits.values()).map(async (editGroup) => {
      editGroup.forEach(([key]) => {
        smartPlannerMode.markInflight(key);
        allEditKeys.push(key);
      });

      const payload = createPayload(editGroup, calendarGranularity ?? CalendarGranularity.Daily);

      try {
        const response = await replaceRange(payload);
        responses.push(response);
      } catch {
        editGroup.forEach(([key]) => {
          smartPlannerMode.revertEdit(key);
          failedKeys.add(key);
        });
      }
    });

    await Promise.all(mutationPromises);

    const createdIds = responses.flatMap(({ createdBookings }) => createdBookings.map(({ id }) => id));
    const deletedIds = responses.flatMap(({ deletedBookings }) => deletedBookings.map(({ id }) => id));

    if (createdIds.length > 0) {
      smartPlannerMode.addUpdatableBookingIds(createdIds);
    }
    if (deletedIds.length > 0) {
      smartPlannerMode.removeUpdatableBookingIds(deletedIds);
    }

    const successfulKeys = allEditKeys.filter((key) => !failedKeys.has(key));

    smartPlannerMode.clearEdit(successfulKeys);
  }, [store, isSmartPlannerView, smartPlannerMode, replaceRange, mutation, calendarGranularity]);

  const debouncedSync = useDebounce(executeSync, SYNC_DEBOUNCE_MS, { maxWait: SYNC_MAX_WAIT_MS });

  useEffect(() => {
    let previousPendingEdits = store.getState().smartPlannerMode.pendingEdits;

    const unsubscribe = store.subscribe((state) => {
      const currentPendingEdits = state.smartPlannerMode.pendingEdits;

      if (currentPendingEdits !== previousPendingEdits) {
        previousPendingEdits = currentPendingEdits;
        debouncedSync();
      }
    });

    return () => {
      debouncedSync.flush();
      unsubscribe();
    };
  }, [store, debouncedSync]);

  useEffect(() => {
    if (!isSmartPlannerView) {
      debouncedSync.flush();
    }

    return () => {
      debouncedSync.flush();
    };
  }, [isSmartPlannerView, debouncedSync]);
};
